import { Component, OnInit, Input } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/shared/services/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { ApiService } from 'src/app/shared/services/api.service';
import { Rol } from 'src/app/model/rol';
import { OptionSesion } from 'src/app/model/optionSesion';
import { ToastrService } from 'ngx-toastr';
import { SessionTimerService } from 'session-expiration-alert';
import { Parametros } from 'src/app/model/parametros';
import { Response } from '../shared/models/response.model';
import { environment } from 'src/environments/environment.prod';

@Component({
  selector: 'app-admin',
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.css']
})
export class AdminComponent implements OnInit {
  assetsUrl: string = environment.baseAssetsUrl;
  color: string = 'black';
  corporateServices: string = 'servicioscorporativos@becompliance.es';

  @Input() menuNavbar: boolean;
  @Input() IsHiddenLanding: boolean;

  // UI states
  isVisible = true;
  mostrar = false;
  showMenu1 = false;
  showMenu2 = true;
  showMenu3 = false;
  isAgrupado = false;

  // Auth / Permits
  IsAuth = false;
  roles: Rol[] = [];
  option: OptionSesion[];
  parametros: Parametros[];

  hasPermit = false;
  hasPermitDenuncia = false;
  hasPermitSADM = false;
  hasPermitAllDenuncias = false;

  bloque1 = false;
  bloque2 = false;
  bloque3 = false;
  showDashboard = false;

  constructor(
    private auth: AuthService,
    private api: ApiService,
    private translate: TranslateService,
    private toastr: ToastrService,
    public sessionTimer: SessionTimerService,
    private router: Router
  ) {}

  ngOnInit() {
    this.initializeSidebarState();
    this.listenToSidebarObservable();
    this.initializeAuthState();

    if (!this.auth.getIdUser()) {
      this.isVisible = false;
      this.IsHiddenLanding = true;
      return;
    }

    this.auth.getSociedad()
      ? this.loadRoles()
      : this.loadSessionAndSetup();
  }

  private initializeSidebarState() {
    const centers = this.auth.getCentros();
    const shouldShow = centers === '1' || this.mostrar === true;
    this.auth.openSidebar(!!shouldShow);
  }

  private listenToSidebarObservable() {
    this.auth.sidebarOpened$
      .subscribe(res => this.mostrar = res);
  }

  private initializeAuthState() {
    this.IsAuth = this.auth.isLoggedIn();

    this.IsHiddenLanding = !this.IsAuth;

    const lang = this.auth.getLanguage() || 'es';
    this.translate.setDefaultLang(lang);
    this.translate.use(lang);
  }

  /**
   * Loads session properties and sets up the application state accordingly.
   */
  private loadSessionAndSetup() {
    this.api.getSessionPropertiesUser().subscribe({
      next: (option: OptionSesion[]) => {
        if (!option[0].id_sociedad) {
          return this.toastr.error(
            this.translate.instant('texto_242'),
            this.translate.instant('texto_243')
          );
        }

        this.option = option;

        this.auth.setLanguage(option[0].idioma);
        this.translate.use(option[0].idioma);
        this.auth.setSociedad(option[0].id_sociedad);
        this.auth.setNameSociedad(option[0].n_sociedad);

        this.loadParametros(option[0].id_sociedad);
        this.loadRoles();
      },
      error: () => {
        this.toastr.error(
          this.translate.instant('texto_242'),
          this.translate.instant('texto_813')
        );
      }
    });
  }

  /**
   * Loads application parameters based on the provided society.
   * @param sociedad
   */
  private loadParametros(sociedad: string) {
    this.api.searchAdvancedParam2(sociedad).subscribe({
      next: (response: Response<Parametros[]>) => {
        if (!response.success || response.code !== 200) {
          if (response.code === 404) {
            this.toastr.error(this.translate.instant('texto_899'));
          }
          return;
        }

        this.parametros = response.data;
        this.applyParametros();
      },
      error: () => {
        this.toastr.error(
          this.translate.instant('texto_890'),
          this.translate.instant('texto_813')
        );
      }
    });
  }

  private applyParametros() {
    const p = this.parametros;

    this.auth.setAnonimo(p[0].valor === '1' ? '1' : '');
    this.auth.setRemitente(p[1].valor);
    this.auth.setConsultas(p[2].valor);
    this.auth.setAviso(p[3].valor);
    this.auth.setArchdenuncia(p[4].valor);

    this.auth.openSidebar(this.auth.getCentros() === '1');
  }

  /**
   * Loads user roles and processes them to determine permissions.
   */
  private loadRoles() {
    this.api.searchRoleByUserSociety('setUser', this.auth.getSociedad(), '1').subscribe({
      next: roles => {
        if (roles.code === 200 && roles.success) {
          this.roles = roles.data;
          this.processRoles();
          this.loadParametros(this.auth.getSociedad());
        }
      },
      error: () => {
        this.toastr.error(
          this.translate.instant('texto_915'),
          this.translate.instant('texto_813')
        );
      }
    });
  }

  private processRoles() {
    const roleList = this.roles.map(r => r.id_rol);
    const roleString = ',' + roleList.join(',');

    this.showDashboard = false;
    // Admin / Superadmin
    if (roleString.includes(',5') || roleString.includes(',99')) {
      this.applyAdminPermits(roleString);
    }

    // Roles: 3, 1, 11, 2, 4
    if (
      roleString.includes(',3') ||
      roleString.includes(',1') ||
      roleString.includes(',11') ||
      roleString.includes(',2') ||
      roleString.includes(',4')
    ) {
      this.hasPermitDenuncia = true;
      this.bloque1 = false;
      this.hasPermitSADM = false;

      if (roleString.includes(',4')) { // Compliance
        this.hasPermitAllDenuncias = true;
        this.bloque3 = true;
        this.showDashboard = true;
      }
    }
  }

  private applyAdminPermits(roleString: string) {
    this.hasPermit = true;
    this.bloque1 = true;
    this.bloque2 = true;
    this.showDashboard = true;

    const isSADM = roleString.includes(',99');

    if (isSADM || this.auth.getIdUserSession() === this.corporateServices) {
      this.auth.setShowAdvOpts(true);
    }

    if (isSADM) {
      this.bloque1 = false;
      this.bloque3 = true;
      this.hasPermitSADM = true;
      this.hasPermitDenuncia = false;
      localStorage.setItem('esSADM', '1');
    }

    this.auth.openSidebar(this.auth.getCentros() === '1');
  }

  async logout() {
    if (localStorage.getItem("forzarloSSO") == "1") {
      this.api.logoutSingleSignOn(
        localStorage.getItem("session_index"),
        localStorage.getItem("nameid_value"),
        localStorage.getItem("nameid_format")
      );
      return;
    }

    this.router.navigate(['/inicio']);
    await this.auth.logoutSimple();
    this.IsHiddenLanding = true;
  }

  showMenu() {
    this.isAgrupado = !this.isAgrupado;

    const panel = document.getElementById('main-panel');
    const btn = document.getElementById('btnAgrupado');
    const links = document.getElementsByClassName('nav-link');

    if (!panel || !btn) return;

    panel.style.zIndex = '999';

    if (this.isAgrupado) {
      panel.style.width = 'calc(100% - 60px)';
      this.updateLinkMargins(links, '0px');
      setTimeout(() => btn.style.left = '10px', 150);
    } else {
      panel.style.width = 'calc(100% - 16.2rem)';
      this.updateLinkMargins(links, '14px');
      setTimeout(() => btn.style.left = '200px', 150);
    }
  }

  private updateLinkMargins(links: HTMLCollectionOf<Element>, margin: string) {
    Array.from(links).forEach(el => {
      el.setAttribute('style', `margin-left:${margin}`);
    });
  }

  hidePanel() {
    this.isVisible = false;
  }

  changeMenu1() { this.showMenu1 = !this.showMenu1; }
  changeMenu2() { this.showMenu2 = !this.showMenu2; }
  changeMenu3() { this.showMenu3 = !this.showMenu3; }
}
